<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import Tag from 'primevue/tag'
import { apiClient } from '@/api/client'
import { useFavoritesStore } from '@/stores/favorites'
import { useGlobalSearchStore } from '@/stores/globalSearch'
import { useAuthStore } from '@/stores/auth'
import { useMenuStore, type MenuNode } from '@/stores/menu'
import { titoloVoceHub, useHubVoci } from '@/composables/useHubVoci'
import { iconaClasseSicura, routeSicura } from '@/modules/configurazione/lib/validazione'
import type { ConfigurazioneVoce } from '@/modules/configurazione/composables/useConfigurazioneHub'

interface PaletteItem {
  key: string
  tipo: 'PAGINA' | 'OSPITE' | 'PERSONALE' | 'TICKET'
  titolo: string
  sottotitolo?: string | null
  icona: string
  legacy: boolean
  target: string
}

interface BackendHit {
  tipo: string
  id: string
  titolo: string
  sottotitolo: string | null
  icona: string
  legacy: boolean
  target: string
}

const store = useGlobalSearchStore()
const menuStore = useMenuStore()
const auth = useAuthStore()
const favoritesStore = useFavoritesStore()
const router = useRouter()
const { t, te } = useI18n()

const query = ref('')
const inputRef = ref<InstanceType<typeof InputText> | null>(null)
const loading = ref(false)
const ospiti = ref<PaletteItem[]>([])
const personale = ref<PaletteItem[]>([])
const ticket = ref<PaletteItem[]>([])
const activeIndex = ref(0)

const legacyBase = import.meta.env.VITE_LEGACY_BASE_URL ?? '/legacy'

// --- Registry pagine (voci di menu + pagine non a menu / report futuri) ---
function flattenMenu(nodes: MenuNode[]): PaletteItem[] {
  const out: PaletteItem[] = []
  for (const n of nodes) {
    if (n.url) {
      out.push({
        key: `menu-${n.id}`,
        tipo: 'PAGINA',
        titolo: te(`menu.${n.id}`) ? t(`menu.${n.id}`) : n.label,
        sottotitolo: null,
        icona: n.icon || 'pi pi-compass',
        legacy: n.isLegacy,
        target: n.url,
      })
    }
    if (n.children?.length) out.push(...flattenMenu(n.children))
  }
  return out
}

// Le pagine di configurazione non stanno più nell'albero del menu: si cercano dalle voci dell'hub.
const { voci: vociHub, vociAdmin: vociHubAdmin } = useHubVoci()
function vociPalette(voci: ConfigurazioneVoce[], admin: boolean): PaletteItem[] {
  return voci
    .filter((v) => !v.classico && routeSicura(v.route) !== null)
    .map((v) => ({
      key: `hub${admin ? '-admin' : ''}-${v.idFunzione}`,
      tipo: 'PAGINA' as const,
      titolo: titoloVoceHub(v, t, te),
      sottotitolo: admin ? t('layout.palette.configurazioneAdmin', 'Configurazione Admin') : t('layout.palette.configurazione', 'Configurazione'),
      icona: iconaClasseSicura(v.icona),
      legacy: false,
      target: v.route as string,
    }))
}
const pagineHub = computed<PaletteItem[]>(() => [...vociPalette(vociHub.value, false), ...vociPalette(vociHubAdmin.value, true)])

const pagine = computed<PaletteItem[]>(() => {
  const q = query.value.trim().toLowerCase()
  if (q.length < 1) return []
  const vistiTarget = new Set<string>()
  return [...flattenMenu(menuStore.items), ...pagineHub.value]
    .filter((p) => !vistiTarget.has(p.target) && vistiTarget.add(p.target))
    .filter((p) => p.titolo.toLowerCase().includes(q))
    .slice(0, 6)
})

// --- Gruppi e lista piatta per navigazione tastiera ---
const recentiItems = computed<PaletteItem[]>(() =>
  store.recenti.map((r, i) => ({ ...r, key: `rec-${i}` })),
)
const preferitiItems = computed<PaletteItem[]>(() =>
  (Array.isArray(favoritesStore.items) ? favoritesStore.items : [])
    .filter((f) => typeof f?.path === 'string' && routeSicura(f.path) !== null && typeof f.label === 'string')
    .map((f, i) => ({
    key: `fav-${i}`, tipo: 'PAGINA', titolo: f.label, sottotitolo: null, icona: 'pi pi-star-fill', legacy: false, target: f.path,
  })),
)
const queryVuota = computed(() => query.value.trim().length < 1)
const mostraRecenti = computed(() => queryVuota.value && (recentiItems.value.length > 0 || preferitiItems.value.length > 0))

const gruppi = computed(() => {
  if (mostraRecenti.value) {
    return [
      { key: 'preferiti', label: t('layout.topbar.preferiti', 'Preferiti'), icon: 'pi pi-star', items: preferitiItems.value },
      { key: 'recenti', label: t('layout.palette.recenti', 'Ultime ricerche'), icon: 'pi pi-history', items: recentiItems.value },
    ].filter((g) => g.items.length > 0)
  }
  return [
    { key: 'pagine', label: t('layout.palette.pagineMenu', 'Pagine / Menu'), icon: 'pi pi-compass', items: pagine.value },
    { key: 'ospiti', label: t('layout.palette.ospiti', 'Ospiti'), icon: 'pi pi-home', items: ospiti.value },
    { key: 'personale', label: t('layout.palette.personale', 'Personale'), icon: 'pi pi-id-card', items: personale.value },
    { key: 'ticket', label: t('layout.palette.ticket', 'Ticket'), icon: 'pi pi-ticket', items: ticket.value },
  ].filter((g) => g.items.length > 0)
})

const flat = computed<PaletteItem[]>(() => gruppi.value.flatMap((g) => g.items))

// --- Ricerca backend (ospiti + personale), debounced ---
let debounce: ReturnType<typeof setTimeout> | null = null

watch(query, (q) => {
  activeIndex.value = 0
  if (debounce) clearTimeout(debounce)
  if (q.trim().length < 2) {
    ospiti.value = []
    personale.value = []
    ticket.value = []
    loading.value = false
    return
  }
  loading.value = true
  debounce = setTimeout(fetchBackend, 250)
})

async function fetchBackend() {
  const q = query.value.trim()
  if (q.length < 2) return
  try {
    const { data } = await apiClient.get('/api/v2/search', { params: { q } })
    ospiti.value = (data.ospiti ?? []).map((h: BackendHit, i: number) => toItem(h, `osp-${i}`))
    personale.value = (data.personale ?? []).map((h: BackendHit, i: number) => toItem(h, `per-${i}`))
    ticket.value = (data.ticket ?? []).map((h: BackendHit, i: number) => toItem(h, `tck-${i}`))
  } catch {
    ospiti.value = []
    personale.value = []
    ticket.value = []
  } finally {
    loading.value = false
  }
}

function toItem(h: BackendHit, key: string): PaletteItem {
  return {
    key,
    tipo: h.tipo as PaletteItem['tipo'],
    titolo: h.titolo,
    sottotitolo: h.sottotitolo,
    icona: h.icona,
    legacy: h.legacy,
    target: h.target,
  }
}

// --- Navigazione ---
function vai(item: PaletteItem) {
  if (!item.key.startsWith('fav-')) store.registra(item)
  store.close()
  if (item.legacy) {
    window.location.href = `${legacyBase}/home.seam?pagina=${encodeURIComponent(item.target)}`
  } else {
    void router.push(item.target)
  }
}

function tipoLabel(tipo: string): string {
  if (tipo === 'OSPITE') return t('layout.palette.ospite', 'Ospite')
  if (tipo === 'PERSONALE') return t('layout.palette.personale', 'Personale')
  if (tipo === 'TICKET') return t('layout.palette.ticket', 'Ticket')
  return t('layout.palette.pagina', 'Pagina')
}
function tipoSeverity(tipo: string): string {
  if (tipo === 'OSPITE') return 'info'
  if (tipo === 'PERSONALE') return 'success'
  if (tipo === 'TICKET') return 'warn'
  return 'secondary'
}

// --- Tastiera ---
function onKeydown(e: KeyboardEvent) {
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    if (flat.value.length) activeIndex.value = (activeIndex.value + 1) % flat.value.length
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    if (flat.value.length) activeIndex.value = (activeIndex.value - 1 + flat.value.length) % flat.value.length
  } else if (e.key === 'Enter') {
    e.preventDefault()
    const item = flat.value[activeIndex.value]
    if (item) vai(item)
  } else if (e.key === 'Escape') {
    store.close()
  }
}

// indice piatto → per evidenziare la riga attiva
function flatIndexOf(item: PaletteItem): number {
  return flat.value.findIndex((i) => i.key === item.key)
}

// --- Apertura: focus + reset ---
watch(
  () => store.isOpen,
  (open) => {
    if (open) {
      store.carica(auth.me?.username)
      query.value = ''
      ospiti.value = []
      personale.value = []
      activeIndex.value = 0
      nextTick(() => {
        // @ts-expect-error — PrimeVue InputText espone $el
        inputRef.value?.$el?.focus()
      })
    }
  },
)

// --- Shortcut globale Ctrl/Cmd+K ---
function onGlobalKey(e: KeyboardEvent) {
  if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
    e.preventDefault()
    store.toggle()
  }
}
onMounted(() => window.addEventListener('keydown', onGlobalKey))
onUnmounted(() => window.removeEventListener('keydown', onGlobalKey))
</script>

<template>
  <Dialog
    v-model:visible="store.isOpen"
    modal
    :closable="false"
    :show-header="false"
    :dismissable-mask="true"
    position="top"
    :style="{ width: '40rem', maxWidth: '95vw' }"
    :breakpoints="{ '640px': '95vw' }"
    :pt="{ content: { class: 'palette-pt-content' } }"
  >
    <div class="palette-search">
      <i class="pi pi-search palette-search-icon" />
      <InputText
        ref="inputRef"
        v-model="query"
        :placeholder="t('layout.palette.placeholder', 'Cerca ospiti, personale, ticket, pagine…')"
        class="palette-input"
        @keydown="onKeydown"
      />
      <i
        v-if="loading"
        class="pi pi-spin pi-spinner palette-search-icon"
      />
      <span
        v-else
        class="palette-kbd"
      >esc</span>
    </div>

    <div class="palette-results">
      <p
        v-if="query.trim().length < 2 && pagine.length === 0 && !mostraRecenti"
        class="palette-hint"
      >
        {{ t('layout.palette.suggerimento', 'Digita almeno 2 caratteri. Cercando Mario Rossi vedrai se è un ospite, un operatore o una pagina; con un codice come TCK-00123, o una parola letta in una segnalazione, trovi il ticket.') }}
      </p>

      <p
        v-else-if="flat.length === 0 && !loading"
        class="palette-hint"
      >
        {{ t('layout.palette.nessunRisultato', { q: query }, 'Nessun risultato per “{q}”.') }}
      </p>

      <template
        v-for="g in gruppi"
        :key="g.key"
      >
        <div class="palette-group-label">
          <i :class="g.icon" /> {{ g.label }}
          <button
            v-if="g.key === 'recenti'"
            type="button"
            class="palette-clear"
            @click="store.svuotaRecenti()"
          >
            {{ t('layout.palette.svuotaRecenti', 'Cancella') }}
          </button>
        </div>
        <button
          v-for="item in g.items"
          :key="item.key"
          type="button"
          class="palette-row"
          :class="{ active: flatIndexOf(item) === activeIndex }"
          @click="vai(item)"
          @mousemove="activeIndex = flatIndexOf(item)"
        >
          <i
            :class="item.icona"
            class="palette-row-icon"
          />
          <span class="palette-row-text">
            <span class="palette-row-title">{{ item.titolo }}</span>
            <span
              v-if="item.sottotitolo"
              class="palette-row-sub"
            >{{ item.sottotitolo }}</span>
          </span>
          <Tag
            :value="tipoLabel(item.tipo)"
            :severity="tipoSeverity(item.tipo)"
            class="palette-row-tag"
          />
          <i
            v-if="item.legacy"
            class="pi pi-external-link palette-row-legacy"
            :title="t('layout.palette.portaleClassico', 'Si apre nel portale classico')"
          />
        </button>
      </template>
    </div>
  </Dialog>
</template>

<style scoped>
:deep(.palette-pt-content) {
  padding: 0;
}
.palette-search {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--p-surface-200);
}
.palette-search-icon {
  color: var(--p-surface-500);
  font-size: 1.1rem;
}
.palette-input {
  flex: 1;
  border: none !important;
  box-shadow: none !important;
  font-size: 1.05rem;
  padding: 0.25rem 0;
  background: transparent;
}
.palette-kbd {
  font-size: 0.7rem;
  color: var(--p-surface-500);
  border: 1px solid var(--p-surface-300);
  border-radius: 0.35rem;
  padding: 0.1rem 0.4rem;
}
.palette-results {
  max-height: 24rem;
  overflow-y: auto;
  padding: 0.5rem;
}
.palette-hint {
  color: var(--p-surface-500);
  padding: 1rem 0.75rem;
  margin: 0;
  font-size: 0.9rem;
}
.palette-group-label {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--p-surface-500);
  padding: 0.6rem 0.75rem 0.3rem;
}
.palette-clear {
  margin-left: auto;
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--p-primary-500);
  font-size: 0.72rem;
  text-transform: none;
  letter-spacing: 0;
}
.palette-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  text-align: left;
  background: transparent;
  border: none;
  border-radius: 0.5rem;
  padding: 0.55rem 0.75rem;
  cursor: pointer;
  color: inherit;
}
.palette-row.active {
  background: var(--p-primary-50);
}
.palette-row-icon {
  color: var(--p-primary-500);
  font-size: 1.05rem;
  width: 1.25rem;
  text-align: center;
}
.palette-row-text {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}
.palette-row-title {
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.palette-row-sub {
  font-size: 0.8rem;
  color: var(--p-surface-500);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.palette-row-tag {
  flex-shrink: 0;
}
.palette-row-legacy {
  color: var(--p-surface-400);
  font-size: 0.85rem;
}
</style>
